import { useRef, useState } from "react";
import { FileDownload, ChevronRight, PictureAsPdf, TableView, Code, DataObject, Html } from "@mui/icons-material";
import { Menu, MenuItem, ListItemIcon, ListItemText, ListSubheader } from "@mui/material";
import Button from "e.sic-platform.webclient/src/components/button/Button";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import useThemeService from 'e.sic-platform.webclient/src/services/theme-service/ThemeService';

const t = (key: string) => translationService.translate(key);

/**
 * One labelled block of exports — the same inspection as described by a single source: the
 * machine's own output, or the reviewer-approved list.
 *
 * Every handler is optional and an omitted one drops its row, so a group offers only what
 * actually exists behind it. The approved OCR list has no standards documents of its own on
 * the server, for instance, so it is declared without the XML handlers and grows no XML row.
 */
export interface ExportGroup {
  /** Translation key for the section heading. */
  labelKey: string;
  onExportPdf?(): void;
  onExportCsv?(): void;
  /** A standalone HTML report: the analyzer's own for the machine group, ours for the approved one. */
  onExportHtml?(): void;
  /** The results in the AI pipeline's own JSON shape. Only the approved copy has one. */
  onExportJson?(): void;
  onExportIsybau?(): void;
  onExportDwa?(): void;
}

interface ExportMenuButtonProps {
  id: string;
  /** Translation key for the button's own label. */
  textKey: string;
  disabled?: boolean;
  groups: ExportGroup[];
}

/**
 * The export menu for one set of results.
 *
 * The formats are the same wherever they appear (PDF, CSV, and the two XML standards), and it
 * is the source that varies, so the menu is driven by the groups it is handed rather than by a
 * show-flag per item: offering another source means declaring another group, not threading
 * another pair of booleans through.
 */
export default function ExportMenuButton({ id, textKey, disabled, groups }: ExportMenuButtonProps) {
  const { themeClass } = useThemeService();
  const buttonRef = useRef<HTMLDivElement>(null);
  // Each group carries its own XML row, so the submenu has to remember which row opened it
  // instead of hanging off one shared ref.
  const xmlRefs = useRef<Array<HTMLLIElement | null>>([]);
  const [mainAnchor, setMainAnchor] = useState<null | HTMLElement>(null);
  const [xmlGroup, setXmlGroup] = useState<number | null>(null);

  const closeAll = () => {
    setXmlGroup(null);
    setMainAnchor(null);
  };

  const call = (cb?: () => void) => {
    cb?.();
    closeAll();
  };

  const openGroup = xmlGroup === null ? undefined : groups[xmlGroup];

  return (
    <div ref={buttonRef}>
      <Button
        id={id}
        onClick={() => setMainAnchor(buttonRef.current)}
        disabled={disabled}
        icon={{
          svg: FileDownload,
          textKey,
          textClass: "actions-header-button-label",
          className: themeClass(["icon-mui"]),
        }}
      />
      <Menu anchorEl={mainAnchor} open={Boolean(mainAnchor)} onClose={closeAll}>
        {groups.flatMap((group, index) => {
          const rows = [
            // Not sticky: a heading that detaches from its items while the menu scrolls loses
            // exactly the association the grouping is there to show.
            <ListSubheader key={`${group.labelKey}-heading`} disableSticky>
              {t(group.labelKey)}
            </ListSubheader>,
          ];

          if (group.onExportPdf) {
            rows.push(
              <MenuItem key={`${group.labelKey}-pdf`} onClick={() => call(group.onExportPdf)}>
                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                <ListItemText>{t("general.export.pdf")}</ListItemText>
              </MenuItem>
            );
          }

          if (group.onExportCsv) {
            rows.push(
              <MenuItem key={`${group.labelKey}-csv`} onClick={() => call(group.onExportCsv)}>
                <ListItemIcon><TableView fontSize="small" /></ListItemIcon>
                <ListItemText>{t("general.export.csv")}</ListItemText>
              </MenuItem>
            );
          }

          if (group.onExportHtml) {
            rows.push(
              <MenuItem key={`${group.labelKey}-html`} onClick={() => call(group.onExportHtml)}>
                <ListItemIcon><Html fontSize="small" /></ListItemIcon>
                <ListItemText>{t("general.export.html")}</ListItemText>
              </MenuItem>
            );
          }

          if (group.onExportJson) {
            rows.push(
              <MenuItem key={`${group.labelKey}-json`} onClick={() => call(group.onExportJson)}>
                <ListItemIcon><DataObject fontSize="small" /></ListItemIcon>
                <ListItemText>{t("general.export.json")}</ListItemText>
              </MenuItem>
            );
          }

          if (group.onExportIsybau || group.onExportDwa) {
            rows.push(
              <MenuItem
                key={`${group.labelKey}-xml`}
                ref={(el: HTMLLIElement | null) => { xmlRefs.current[index] = el; }}
                onClick={() => setXmlGroup(index)}
              >
                <ListItemIcon><Code fontSize="small" /></ListItemIcon>
                <ListItemText>{t("general.export.xml")}</ListItemText>
                <ChevronRight fontSize="small" />
              </MenuItem>
            );
          }

          return rows;
        })}
      </Menu>
      <Menu
        anchorEl={xmlGroup === null ? null : xmlRefs.current[xmlGroup]}
        open={xmlGroup !== null}
        onClose={() => setXmlGroup(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {openGroup?.onExportIsybau && (
          <MenuItem onClick={() => call(openGroup.onExportIsybau)}>ISYBAU_2017-07</MenuItem>
        )}
        {openGroup?.onExportDwa && (
          <MenuItem onClick={() => call(openGroup.onExportDwa)}>DWA-M150_04-2010</MenuItem>
        )}
      </Menu>
    </div>
  );
}
